import React from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Button, CircularProgress } from '@mui/material';
import { Icon } from '@iconify/react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const StatisticsPDF = ({ statistics, period, startDate, endDate }) => {
  const [isGenerating, setIsGenerating] = React.useState(false);

  const generatePDF = async () => {
    setIsGenerating(true);
    
    try {
      // Créer un élément temporaire pour le contenu PDF
      const pdfContent = document.createElement('div');
      pdfContent.style.width = '650px';
      pdfContent.style.padding = '10px';
      pdfContent.style.margin = '5px';
      pdfContent.style.backgroundColor = 'white';
      pdfContent.style.fontFamily = 'Arial, sans-serif';
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.top = '0';
      pdfContent.style.boxSizing = 'border-box';
      
      // Générer le contenu HTML pour le PDF
      const content = generatePDFContent();
      pdfContent.innerHTML = content;
      
      document.body.appendChild(pdfContent);
      
      // Convertir en canvas puis en PDF
      const canvas = await html2canvas(pdfContent, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      document.body.removeChild(pdfContent);
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Marges de protection pour l'impression (en mm)
      const marginTop = 10;
      const marginBottom = 25;
      const marginLeft = 15;
      const marginRight = 15;
      
      // Zone utilisable du PDF
      const usableWidth = pdfWidth - marginLeft - marginRight;
      const usableHeight = pdfHeight - marginTop - marginBottom;
      
      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Calculer combien de pages sont nécessaires
      const pagesNeeded = Math.ceil(imgHeight / usableHeight);
      
      if (pagesNeeded === 1) {
        // Une seule page : afficher l'image complète
        pdf.addImage(imgData, 'PNG', marginLeft, marginTop, imgWidth, imgHeight);
      } else {
        // Plusieurs pages : découper l'image
        for (let page = 0; page < pagesNeeded; page++) {
          if (page > 0) {
            pdf.addPage();
          }
          
          const sourceY = page * usableHeight * (canvas.height / imgHeight);
          const sourceHeight = Math.min(usableHeight * (canvas.height / imgHeight), canvas.height - sourceY);
          
          // Créer un nouveau canvas pour cette page
          const pageCanvas = document.createElement('canvas');
          const ctx = pageCanvas.getContext('2d');
          pageCanvas.width = canvas.width;
          pageCanvas.height = sourceHeight;
          
          // Copier la partie correspondante de l'image
          ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
          
          const pageImgData = pageCanvas.toDataURL('image/png');
          const pageImgHeight = (sourceHeight * usableWidth) / canvas.width;
          
          pdf.addImage(pageImgData, 'PNG', marginLeft, marginTop, usableWidth, pageImgHeight);
        }
      }
      
      // Générer le nom du fichier avec les dates de période en français
      let fileName;
      if (period === 'all') {
        fileName = `statistiques_VC_${format(new Date(), 'dd-MM-yyyy_HH-mm')}.pdf`;
      } else {
        const startDateStr = format(new Date(startDate), 'dd-MM-yyyy_HH-mm');
        const endDateStr = format(new Date(endDate), 'dd-MM-yyyy_HH-mm');
        fileName = `statistiques_VC_du_${startDateStr}_au_${endDateStr}.pdf`;
      }
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePDFContent = () => {
    const global = statistics?.global || {};
    const categories = statistics?.categories || [];
    
    const totalFound = parseInt(global.found_returned || 0) + 
                      parseInt(global.found_sent || 0) + 
                      parseInt(global.found_to_send || 0) + 
                      parseInt(global.found_paid || 0) + 
                      parseInt(global.found_found || 0);
    
    const totalRestituted = parseInt(global.found_returned || 0) + 
                           parseInt(global.lost_returned || 0);
    
    const restitutionRate = global.total_annonces > 0 
      ? Math.round((totalRestituted / parseInt(global.total_annonces)) * 100)
      : 0;

    const periodText = period === 'all' ? 'Toutes périodes' :
                      period === 'week' ? '7 derniers jours' :
                      period === 'month' ? 'Ce mois' :
                      period === 'year' ? 'Cette année' : 'Période personnalisée';

    return `
      <div style="font-family: Arial, sans-serif; color: #333; max-width: 100%; margin: 0 auto; padding: 0;">
        <!-- En-tête avec logo customer -->
        <div style="text-align: center; margin-bottom: 15px; border-bottom: 2px solid #1b9098; padding-bottom: 10px; margin-top: 0;">
          <div style="margin-bottom: 8px;">
            <img src="${process.env.REACT_APP_BASEURL}${process.env.REACT_APP_CUSTOMERLOGO}" alt="Logo" style="height: 50px; max-width: 180px; object-fit: contain;" onerror="this.style.display='none';" />
            <div style="font-size: 16px; font-weight: bold; color: #1b9098; margin-top: 3px;">{process.env.REACT_APP_FESTIVALNAME}</div>
          </div>
          <h1 style="color: #1b9098; margin: 0; font-size: 20px;">Rapport des Statistiques</h1>
          <p style="color: #666; margin: 4px 0 0 0; font-size: 12px;">Période : ${periodText}</p>
          ${period !== 'all' ? `<p style="color: #666; margin: 1px 0 0 0; font-size: 10px;">Du ${format(new Date(startDate), 'dd/MM/yyyy HH:mm', { locale: fr })} au ${format(new Date(endDate), 'dd/MM/yyyy HH:mm', { locale: fr })}</p>` : ''}
          <p style="color: #666; margin: 1px 0 0 0; font-size: 10px;">Généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })}</p>
        </div>

        <!-- Statistiques globales -->
        <div style="margin-bottom: 15px;">
          <h2 style="color: #1b9098; font-size: 16px; margin-bottom: 10px;">Statistiques Globales</h2>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
            <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; border-left: 3px solid #007bff;">
              <h3 style="margin: 0 0 6px 0; color: #007bff; font-size: 12px;">Total des annonces</h3>
              <p style="margin: 0; font-size: 18px; font-weight: bold; color: #333;">${global.total_annonces || 0}</p>
            </div>
            <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; border-left: 3px solid #28a745;">
              <h3 style="margin: 0 0 6px 0; color: #28a745; font-size: 12px;">Objets trouvés</h3>
              <p style="margin: 0; font-size: 18px; font-weight: bold; color: #333;">${totalFound}</p>
            </div>
            <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; border-left: 3px solid #ffc107;">
              <h3 style="margin: 0 0 6px 0; color: #ffc107; font-size: 12px;">Objets restitués</h3>
              <p style="margin: 0; font-size: 18px; font-weight: bold; color: #333;">${totalRestituted}</p>
            </div>
            <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; border-left: 3px solid #6f42c1;">
              <h3 style="margin: 0 0 6px 0; color: #6f42c1; font-size: 12px;">Taux de restitution</h3>
              <p style="margin: 0; font-size: 18px; font-weight: bold; color: #333;">${restitutionRate}%</p>
            </div>
          </div>
        </div>

        <!-- Détail des statuts -->
        <div style="margin-bottom: 15px;">
          <h2 style="color: #1b9098; font-size: 16px; margin-bottom: 10px;">Détail des Statuts</h2>
          <div style="background: #f8f9fa; padding: 12px; border-radius: 5px;">
            <div style="margin-bottom: 10px;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
                <span style="font-weight: 500; font-size: 11px;">Restitués</span>
                <span style="font-weight: bold; font-size: 11px;">${global.found_returned || 0}</span>
              </div>
              <div style="background: #e9ecef; height: 5px; border-radius: 2px; overflow: hidden;">
                <div style="background: #28a745; height: 100%; width: ${global.total_annonces > 0 ? (parseInt(global.found_returned || 0) / parseInt(global.total_annonces)) * 100 : 0}%;"></div>
              </div>
            </div>
            <div style="margin-bottom: 10px;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
                <span style="font-weight: 500; font-size: 11px;">En cours d'envoi</span>
                <span style="font-weight: bold; font-size: 11px;">${global.found_sent || 0}</span>
              </div>
              <div style="background: #e9ecef; height: 5px; border-radius: 2px; overflow: hidden;">
                <div style="background: #007bff; height: 100%; width: ${global.total_annonces > 0 ? (parseInt(global.found_sent || 0) / parseInt(global.total_annonces)) * 100 : 0}%;"></div>
              </div>
            </div>
            <div style="margin-bottom: 10px;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
                <span style="font-weight: 500; font-size: 11px;">À expédier</span>
                <span style="font-weight: bold; font-size: 11px;">${global.found_to_send || 0}</span>
              </div>
              <div style="background: #e9ecef; height: 5px; border-radius: 2px; overflow: hidden;">
                <div style="background: #ffc107; height: 100%; width: ${global.total_annonces > 0 ? (parseInt(global.found_to_send || 0) / parseInt(global.total_annonces)) * 100 : 0}%;"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Répartition par catégorie -->
        <div style="margin-bottom: 15px;">
          <h2 style="color: #1b9098; font-size: 16px; margin-bottom: 10px;">Répartition par Catégorie</h2>
          <div style="background: #f8f9fa; padding: 12px; border-radius: 5px;">
            ${categories.map(category => {
              const found = parseInt(category.found_returned || 0) + 
                           parseInt(category.found_sent || 0) + 
                           parseInt(category.found_to_send || 0) + 
                           parseInt(category.found_paid || 0) + 
                           parseInt(category.found_found || 0);
              const restituted = parseInt(category.found_returned || 0);
              const restitutedPercentage = found > 0 ? Math.round((restituted / found) * 100) : 0;
              
              return `
                <div style="margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #dee2e6;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                    <h3 style="margin: 0; font-size: 12px; color: #333;">${category.name}</h3>
                    <span style="font-weight: bold; color: #666; font-size: 10px;">${found} objets trouvés</span>
                  </div>
                  <div style="margin-bottom: 5px;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
                      <span style="font-size: 10px; color: #28a745;">Objets trouvés</span>
                      <span style="font-size: 10px; color: #28a745;">${found}</span>
                    </div>
                    <div style="background: #e9ecef; height: 3px; border-radius: 1px; overflow: hidden;">
                      <div style="background: #28a745; height: 100%; width: 100%;"></div>
                    </div>
                  </div>
                  <div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
                      <span style="font-size: 10px; color: #007bff;">Objets restitués (${restitutedPercentage}%)</span>
                      <span style="font-size: 10px; color: #007bff;">${restituted}</span>
                    </div>
                    <div style="background: #e9ecef; height: 3px; border-radius: 1px; overflow: hidden;">
                      <div style="background: #007bff; height: 100%; width: ${restitutedPercentage}%;"></div>
                    </div>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>

        <!-- Pied de page avec logo Sherlook -->
        <div style="text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px solid #dee2e6; color: #666; padding-bottom: 15px;">
          <div style="margin-bottom: 8px;">
            <img src="${window.location.origin}/static/logo.svg" alt="Logo Sherlook" style="height: 35px; max-width: 120px; object-fit: contain;" onerror="this.style.display='none';" />
            <div style="font-size: 12px; font-weight: bold; color: #1b9098; margin-top: 3px;">SHERLOOK</div>
          </div>
          <p style="margin: 0; font-size: 9px;">Rapport généré automatiquement par le système de gestion des objets trouvés</p>
          <p style="margin: 2px 0 0 0; font-size: 8px; color: #999;">Powered by Sherlook</p>
        </div>
      </div>
    `;
  };

  return (
    <Button
      variant="outlined"
      onClick={generatePDF}
      disabled={isGenerating || !statistics}
      startIcon={
        isGenerating ? (
          <CircularProgress size={16} color="primary" />
        ) : (
          <Icon icon="mdi:file-pdf-box" width={16} height={16} />
        )
      }
      size="small"
      sx={{
        color: '#1976d2',
        borderColor: '#1976d2',
        borderRadius: '6px',
        textTransform: 'none',
        fontSize: '0.875rem',
        padding: '8px 16px',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
          borderColor: '#1565c0',
        },
        '&:disabled': {
          color: 'rgba(0, 0, 0, 0.38)',
          borderColor: 'rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      {isGenerating ? 'Génération...' : 'PDF'}
    </Button>
  );
};

export default StatisticsPDF; 